import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, TypographyStyledGlobal } from '../../theme';
import {
    SelectedQuestion,
    SelectedQuestionResponseSettings,
    SelectedQuestionSettings,
} from '../Modals/AddSurveyModal/addSurveyInterface';
import { ModalStyled } from '../Modals/styled';
import { DragHandleStyled } from './styled';
import {
    CheckboxRowStyled,
    QuestionSettingsBoxStyled,
    ResponseInputStyled,
    ResponseItemStyled,
    ResponseNumericInputStyled,
    ResponsesListStyled,
    SettingsCloseButtonStyled,
    SettingsFooterStyled,
    SettingsHeaderStyled,
    SettingsLabelInputStyled,
    SettingsSectionStyled,
    SettingsSectionTitleStyled,
} from './styledQuestionSettings';

type BoolSettingKey = keyof Pick<
    SelectedQuestionSettings,
    'hasNominalData' | 'hasNumericValues' | 'isExportable' | 'isNps' | 'usableForQuartiles' | 'isDate' | 'hasInitWithLastValue'
>;

interface SortableResponseItemProps {
    hasNumericValues: boolean;
    onChangeCaption: (id: number, value: string) => void;
    onChangeExcluded: (id: number, value: boolean) => void;
    onChangeScaled: (id: number, value: number | null) => void;
    response: SelectedQuestionResponseSettings;
}

const SortableResponseItem: FC<SortableResponseItemProps> = ({
    hasNumericValues,
    onChangeCaption,
    onChangeExcluded,
    onChangeScaled,
    response,
}) => {
    const { t } = useTranslation('settings');
    const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id: response.id });

    const style = {
        opacity: isDragging ? 0.3 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <ResponseItemStyled excluded={response.excluded}>
                <DragHandleStyled {...listeners}>
                    <DragIndicatorIcon sx={{ color: colors.grey400, fontSize: 14 }} />
                </DragHandleStyled>
                <ResponseInputStyled
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCaption(response.id, e.target.value)}
                    value={response.maincaption}
                />
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={response.excluded}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeExcluded(response.id, e.target.checked)}
                            size="small"
                            sx={{ '&.Mui-checked': { color: colors.default }, color: colors.grey400, padding: '4px' }}
                        />
                    )}
                    label={(
                        <TypographyStyledGlobal color={colors.grey400} size={fontSize.s}>
                            {t('tabs.createSurvey.questionConfig.responseExcluded')}
                        </TypographyStyledGlobal>
                    )}
                    sx={{ flexShrink: 0, margin: 0 }}
                />
                {hasNumericValues && (
                    <ResponseNumericInputStyled
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const raw = e.target.value;
                            onChangeScaled(response.id, raw === '' ? null : Number(raw));
                        }}
                        type="number"
                        value={response.scaledresponse ?? ''}
                    />
                )}
            </ResponseItemStyled>
        </div>
    );
};

interface QuestionSettingsModalProps {
    onClose: () => void;
    onSave: (questionId: number, customName: string, settings: SelectedQuestionSettings) => void;
    selectedQuestion: SelectedQuestion;
    hideSettings?: boolean;
    filterMode?: boolean;
}

const QuestionSettingsModal: FC<QuestionSettingsModalProps> = ({ onClose, onSave, selectedQuestion, hideSettings = false, filterMode = false }) => {
    const { t } = useTranslation('settings');

    const [localName, setLocalName] = useState(selectedQuestion.customName);
    const [localSettings, setLocalSettings] = useState<SelectedQuestionSettings>({
        ...selectedQuestion.settings,
        responses: selectedQuestion.settings.responses.map((r) => ({ ...r })),
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = localSettings.responses.findIndex((r) => r.id === active.id);
            const newIndex = localSettings.responses.findIndex((r) => r.id === over.id);

            setLocalSettings((prev) => ({ ...prev, responses: arrayMove(prev.responses, oldIndex, newIndex) }));
        }
    };

    const handleBoolChange = (key: BoolSettingKey, value: boolean) => {
        setLocalSettings((prev) => ({
            ...prev,
            [key]: value,
            ...(key === 'isDate' && !value ? { hasInitWithLastValue: false } : {}),
        }));
    };

    const handleChangeCaption = (id: number, value: string) => {
        setLocalSettings((prev) => ({
            ...prev,
            responses: prev.responses.map((r) => {
                if (r.id === id) { return { ...r, maincaption: value }; }

                return r;
            }),
        }));
    };

    const handleChangeExcluded = (id: number, value: boolean) => {
        setLocalSettings((prev) => ({
            ...prev,
            responses: prev.responses.map((r) => {
                if (r.id === id) { return { ...r, excluded: value }; }

                return r;
            }),
        }));
    };

    const handleChangeScaled = (id: number, value: number | null) => {
        setLocalSettings((prev) => ({
            ...prev,
            responses: prev.responses.map((r) => {
                if (r.id === id) { return { ...r, scaledresponse: value }; }

                return r;
            }),
        }));
    };

    const handleSave = () => {
        onSave(selectedQuestion.question.id, localName, localSettings);
        onClose();
    };

    const { question } = selectedQuestion;
    const hasResponses = localSettings.responses.length > 0;
    const isSingleOrMultiple = question.details.type === 'Single' || question.details.type === 'Multiple';

    const checkboxItems: Array<{ key: BoolSettingKey; label: string; disabled?: boolean }> = filterMode ? [
        { key: 'isDate', label: t('tabs.createSurvey.questionConfig.isDate') },
        { key: 'hasInitWithLastValue', label: t('tabs.createSurvey.questionConfig.hasInitWithLastValue'), disabled: !localSettings.isDate },
    ] : [
        { key: 'hasNominalData', label: t('tabs.createSurvey.questionConfig.hasNominalData') },
        { key: 'isExportable', label: t('tabs.createSurvey.questionConfig.isExportable') },
        ...(isSingleOrMultiple ? [
            { key: 'hasNumericValues' as BoolSettingKey, label: t('tabs.createSurvey.questionConfig.hasNumericValues') },
            { key: 'usableForQuartiles' as BoolSettingKey, label: t('tabs.createSurvey.questionConfig.usableForQuartiles') },
            { key: 'isNps' as BoolSettingKey, label: t('tabs.createSurvey.questionConfig.isNps') },
        ] : []),
    ];

    return (
        <ModalStyled
            aria-describedby="question-settings-description"
            aria-labelledby="question-settings-title"
            onClose={onClose}
            open
        >
            <Fade in>
                <QuestionSettingsBoxStyled>
                    <SettingsCloseButtonStyled onClick={onClose} type="button">
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </SettingsCloseButtonStyled>

                    <SettingsHeaderStyled>
                        <TypographyStyledGlobal color={colors.grey100} size="16px" weight="600">
                            {t('tabs.createSurvey.questionConfig.title')}
                        </TypographyStyledGlobal>
                        <TypographyStyledGlobal color={colors.grey400} size={fontSize.sm}>
                            {question.shortcut}
                        </TypographyStyledGlobal>
                    </SettingsHeaderStyled>

                    <SettingsSectionStyled>
                        <SettingsSectionTitleStyled>
                            {t('tabs.createSurvey.questionConfig.labelSection')}
                        </SettingsSectionTitleStyled>
                        <SettingsLabelInputStyled
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalName(e.target.value)}
                            value={localName}
                        />
                    </SettingsSectionStyled>

                    {!hideSettings && (
                        <SettingsSectionStyled>
                            <SettingsSectionTitleStyled>
                                {t('tabs.createSurvey.questionConfig.settingsSection')}
                            </SettingsSectionTitleStyled>
                            <CheckboxRowStyled>
                                {checkboxItems.map(({ key, label, disabled }) => (
                                    <FormControlLabel
                                        key={key}
                                        control={(
                                            <Checkbox
                                                checked={localSettings[key]}
                                                disabled={disabled}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBoolChange(key, e.target.checked)}
                                                size="small"
                                                sx={{ '&.Mui-checked': { color: colors.default }, color: colors.grey400, padding: '4px' }}
                                            />
                                        )}
                                        label={(
                                            <TypographyStyledGlobal color={disabled ? colors.grey500 : colors.grey300} size={fontSize.sm}>
                                                {label}
                                            </TypographyStyledGlobal>
                                        )}
                                        sx={{ margin: '0 0 2px' }}
                                    />
                                ))}
                            </CheckboxRowStyled>
                        </SettingsSectionStyled>
                    )}

                    {hasResponses && (
                        <SettingsSectionStyled>
                            <SettingsSectionTitleStyled>
                                {t('tabs.createSurvey.questionConfig.responsesSection')}
                            </SettingsSectionTitleStyled>
                            <ResponsesListStyled>
                                <DndContext
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                    sensors={sensors}
                                >
                                    <SortableContext
                                        items={localSettings.responses.map((r) => r.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {localSettings.responses.map((response) => (
                                            <SortableResponseItem
                                                key={response.id}
                                                hasNumericValues={localSettings.hasNumericValues}
                                                onChangeCaption={handleChangeCaption}
                                                onChangeExcluded={handleChangeExcluded}
                                                onChangeScaled={handleChangeScaled}
                                                response={response}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </ResponsesListStyled>
                        </SettingsSectionStyled>
                    )}

                    <SettingsFooterStyled>
                        <Button
                            onClick={onClose}
                            sx={{ '&:hover': { backgroundColor: colors.grey600 }, color: colors.grey300 }}
                        >
                            {t('tabs.createSurvey.questionConfig.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            sx={{
                                '&:hover': { backgroundColor: colors.defaultHover },
                                backgroundColor: colors.default,
                                borderRadius: '6px',
                                color: colors.grey000,
                            }}
                            variant="contained"
                        >
                            {t('tabs.createSurvey.questionConfig.save')}
                        </Button>
                    </SettingsFooterStyled>
                </QuestionSettingsBoxStyled>
            </Fade>
        </ModalStyled>
    );
};

export default QuestionSettingsModal;
